import { Eye, MousePointerClick, Users2, Percent, DollarSign, Target, TrendingUp, Repeat } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export const BRL = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const num = (v) => Number(v || 0).toLocaleString("pt-BR");

function Kpi({ label, value, icon, accent, testid }) {
  return (
    <Card className="p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{icon} {label}</div>
      <div className={`text-xl font-bold tracking-tight mt-0.5 ${accent ? "text-primary" : ""}`}>{value}</div>
    </Card>
  );
}

export function RangePicker({ days, onChange, testPrefix = "ad-analytics-range" }) {
  return (
    <div className="flex gap-2">
      {[7, 14, 30].map((d) => (
        <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => onChange(d)} data-testid={`${testPrefix}-${d}`} className="rounded-full">
          {d} dias
        </Button>
      ))}
    </div>
  );
}

export function KpiGrid({ totals, testPrefix = "ad-analytics" }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`${testPrefix}-kpis`}>
      <Kpi label="Impressões" value={num(totals.impressions)} icon={<Eye className="w-3 h-3" />} testid={`${testPrefix}-total-impressions`} />
      <Kpi label="Alcance" value={num(totals.reach)} icon={<Users2 className="w-3 h-3" />} testid={`${testPrefix}-total-reach`} />
      <Kpi label="Cliques" value={num(totals.clicks)} icon={<MousePointerClick className="w-3 h-3" />} testid={`${testPrefix}-total-clicks`} />
      <Kpi label="CTR" value={`${Number(totals.ctr || 0).toFixed(2)}%`} icon={<Percent className="w-3 h-3" />} accent testid={`${testPrefix}-total-ctr`} />
      <Kpi label="Valor gasto" value={BRL(totals.spent_brl)} icon={<DollarSign className="w-3 h-3" />} accent testid={`${testPrefix}-total-spent`} />
      <Kpi label="Custo por clique" value={BRL(totals.cpc)} icon={<DollarSign className="w-3 h-3" />} testid={`${testPrefix}-total-cpc`} />
      <Kpi label="Conversões" value={num(totals.conversions)} icon={<Target className="w-3 h-3" />} testid={`${testPrefix}-total-conversions`} />
      <Kpi label="Frequência" value={Number(totals.frequency || 0).toFixed(2)} icon={<Repeat className="w-3 h-3" />} testid={`${testPrefix}-total-frequency`} />
    </div>
  );
}

/** Dual-series bar chart (impressions + clicks) with spend per day on hover. */
export function PeriodChart({ series, testPrefix = "ad-analytics" }) {
  const max = series.reduce((m, d) => Math.max(m, d.impressions), 0) || 1;
  const total = series.reduce((s, d) => s + d.impressions, 0);
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Desempenho por período</div>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-primary/70" /> Impressões</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> Cliques</span>
        </div>
      </div>
      {total === 0 ? (
        <div className="h-40 flex items-center justify-center text-sm text-muted-foreground text-center px-4" data-testid={`${testPrefix}-empty`}>
          Sem dados no período. Compartilhe ou defina um orçamento para acumular impressões.
        </div>
      ) : (
        <div className="flex items-end gap-1 h-40" data-testid={`${testPrefix}-chart`}>
          {series.map((d, idx) => {
            const hi = Math.max(2, (d.impressions / max) * 100);
            const hc = d.clicks ? Math.max(2, (d.clicks / max) * 100) : 0;
            const showLabel = series.length <= 14 || idx % Math.ceil(series.length / 10) === 0;
            return (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-1 min-w-0 h-full justify-end"
                title={`${d.day} · ${d.impressions} impressões · ${d.clicks} cliques · ${d.conversions} conversões · ${Number(d.ctr).toFixed(1)}% CTR · ${BRL(d.spent_brl)}`}>
                <div className="w-full flex items-end gap-px" style={{ height: "100%" }}>
                  <div className="flex-1 rounded-t bg-primary/70 hover:bg-primary transition-colors" style={{ height: `${hi}%` }} />
                  <div className="w-1/3 rounded-t bg-emerald-500" style={{ height: `${hc}%` }} />
                </div>
                <div className="text-[9px] text-muted-foreground truncate w-full text-center">{showLabel ? d.day.slice(5).replace("-", "/") : ""}</div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export function PeriodTable({ series, testPrefix = "ad-analytics" }) {
  const rows = [...series].reverse().filter((d) => d.impressions || d.clicks || d.spent_brl);
  if (rows.length === 0) return null;
  return (
    <Card className="p-3 overflow-x-auto" data-testid={`${testPrefix}-table`}>
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr className="text-left border-b border-border">
            <th className="py-1.5 pr-2 font-medium">Dia</th>
            <th className="py-1.5 pr-2 font-medium text-right">Impr.</th>
            <th className="py-1.5 pr-2 font-medium text-right">Cliques</th>
            <th className="py-1.5 pr-2 font-medium text-right">CTR</th>
            <th className="py-1.5 pr-2 font-medium text-right">Conv.</th>
            <th className="py-1.5 font-medium text-right">Gasto</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.day} className="border-b border-border/60 last:border-0 tabular-nums">
              <td className="py-1 pr-2">{d.day.slice(8)}/{d.day.slice(5, 7)}</td>
              <td className="py-1 pr-2 text-right">{num(d.impressions)}</td>
              <td className="py-1 pr-2 text-right">{num(d.clicks)}</td>
              <td className="py-1 pr-2 text-right">{Number(d.ctr).toFixed(2)}%</td>
              <td className="py-1 pr-2 text-right">{num(d.conversions)}</td>
              <td className="py-1 text-right">{BRL(d.spent_brl)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
